import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { eventService } from "../../../services/api/eventService";
import { useAuth } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import { Event } from "../../../types/event";
import MediaSelector from "../MediaSelector";

interface EventCreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (newEvent: Event) => void;
}

const STATUS_OPTIONS: Event["status"][] = ["upcoming", "completed", "cancelled"];

const initialFormState: Partial<Event> = {
  title: "",
  date: "",
  time: "",
  location: "",
  organizer: "",
  status: "upcoming",
  description: "",
  imageUrl: "",
};

const EventCreateModal: React.FC<EventCreateModalProps> = ({
  open,
  onClose,
  onCreate,
}) => {
  const { tokens } = useAuth();
  const [formData, setFormData] = useState<Partial<Event>>(initialFormState);

  // Reset form when the modal opens
  useEffect(() => {
    if (open) {
      setFormData(initialFormState);
    }
  }, [open]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      status: e.target.value as Event["status"],
    }));
  };

  const handleImageSelect = (url: string) => {
    setFormData((prev) => ({ ...prev, imageUrl: url }));
  };

  const handleSubmit = async () => {
    if (!tokens?.idToken) {
      toast.error("User is not authenticated.");
      return;
    }

    try {
      const newEvent = await eventService.createEvent(
        tokens.idToken,
        formData as Event
      );
      onCreate(newEvent);
      toast.success("Event created successfully.");
      onClose();
      // Optionally, reset the form
      setFormData(initialFormState);
    } catch (error) {
      toast.error("Failed to create event.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Event</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label="Date"
            name="date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.date}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label="Time"
            name="time"
            type="time"
            InputLabelProps={{ shrink: true }}
            value={formData.time}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            select
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleStatusChange}
            fullWidth
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Organizer"
            name="organizer"
            value={formData.organizer}
            onChange={handleChange}
            fullWidth
          />
          <MediaSelector 
            mediaType="image"
            label="Event Image"
            initialMediaUrl={formData.imageUrl || ''}
            onSelect={handleImageSelect}
          />
          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={4}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Create
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventCreateModal;
